import React, { useEffect, useRef } from 'react';
import { pointerEngine } from './pointerEngine';
import { playLoop } from './playLoop';
import { PRESETS, Spring, stepSpring, settled } from './springs';
import { useStorm } from '../storm/StormContext';
import { useFpsBackoff } from '../storm/useFpsBackoff';
import './play.css';

const BASE = 22;
const PAD = 6;
const HIDE_SEL = 'textarea,input,[contenteditable]';
const HOVER_EVERY = 4; // ticks between elementFromPoint reads
const STILL_TICKS = 12; // snapped: ticks the target box must hold still before idling

type Axis = { x: Spring; y: Spring; w: Spring; h: Spring };

// Mouse-only overlay companion to the native cursor (which stays visible).
const Reticle: React.FC = () => {
  const root = useRef<HTMLDivElement>(null);
  const { reducedMotion } = useStorm();
  const q = useFpsBackoff();

  useEffect(() => {
    const node = root.current;
    if (!node) return;
    const el: HTMLDivElement = node;
    if (q >= 2) { el.classList.remove('play-reticle--on'); return; }
    const s = pointerEngine.state;
    const a: Axis = { x: { x: -100, v: 0 }, y: { x: -100, v: 0 }, w: { x: BASE, v: 0 }, h: { x: BASE, v: 0 } };
    let seen = false; // first mouse move observed
    let pressed = false;
    let lock = false;
    let shown = false;
    let tickN = 0;
    let hoverEl: Element | null = null;
    let unreg: (() => void) | null = null;
    let splitTimer = 0;
    let stillN = 0; // consecutive ticks at rest with an unchanged snap target box
    let lastBox = '';

    const setShown = (v: boolean) => { if (v !== shown) { shown = v; el.classList.toggle('play-reticle--on', v); } };
    const setLock = (v: boolean) => { if (v !== lock) { lock = v; el.classList.toggle('play-reticle--lock', v); } };

    function tick(dt: number) {
      const mouse = s.type === 'mouse' && seen;
      if (!mouse) { setShown(false); stop(); return; }
      if (tickN++ % HOVER_EVERY === 0) hoverEl = document.elementFromPoint(s.x, s.y);
      if (hoverEl && hoverEl.closest(HIDE_SEL)) { setShown(false); stop(); return; }
      setShown(true);
      let tx = s.x, ty = s.y, tw = BASE, th = BASE;
      const snap = !reducedMotion && hoverEl ? hoverEl.closest('[data-play]') : null;
      if (snap) {
        const r = snap.getBoundingClientRect();
        tx = r.left + r.width / 2; ty = r.top + r.height / 2; tw = r.width + PAD * 2; th = r.height + PAD * 2;
      }
      setLock(!!snap);
      if (reducedMotion) { a.x = { x: tx, v: 0 }; a.y = { x: ty, v: 0 }; }
      else {
        a.x = stepSpring(a.x, tx, dt, PRESETS.snappy); a.y = stepSpring(a.y, ty, dt, PRESETS.snappy);
        a.w = stepSpring(a.w, tw, dt, PRESETS.snappy); a.h = stepSpring(a.h, th, dt, PRESETS.snappy);
      }
      const w = Math.max(8, a.w.x), h = Math.max(8, a.h.x);
      el.style.width = `${w}px`; el.style.height = `${h}px`;
      el.style.transform = `translate3d(${a.x.x - w / 2}px,${a.y.x - h / 2}px,0) scale(${pressed ? 0.85 : 1})`;
      // Idle once everything is at rest (re-armed by move/down/up/scroll/resize). While snapped,
      // also require the target box to hold still for STILL_TICKS (a magnetic target may still
      // be springing back after the pointer stops).
      const box = `${tx},${ty},${tw},${th}`;
      const rest = settled(a.x, tx) && settled(a.y, ty) && settled(a.w, tw) && settled(a.h, th);
      stillN = rest && box === lastBox ? stillN + 1 : 0;
      lastBox = box;
      if (rest && (!snap || stillN >= STILL_TICKS)) stop();
    }
    function start() { if (!unreg) { tickN = 0; stillN = 0; unreg = playLoop.register(tick); } }
    function stop() { if (unreg) { unreg(); unreg = null; } }

    let lastHover = 0;
    const onMove = () => {
      if (s.type !== 'mouse') { setShown(false); stop(); return; }
      if (!seen) { seen = true; a.x = { x: s.x, v: 0 }; a.y = { x: s.y, v: 0 }; }
      // Hover check runs on every (throttled) move even when the spring loop is idle.
      const t = performance.now();
      if (t - lastHover > 40) {
        lastHover = t;
        hoverEl = document.elementFromPoint(s.x, s.y);
        if (hoverEl && hoverEl.closest(HIDE_SEL)) { setShown(false); stop(); return; }
      }
      start();
    };
    const onDown = () => {
      if (s.type !== 'mouse') { setShown(false); stop(); return; }
      pressed = true;
      el.classList.add('play-reticle--split');
      window.clearTimeout(splitTimer);
      splitTimer = window.setTimeout(() => el.classList.remove('play-reticle--split'), 120);
      start();
    };
    const onUp = () => { pressed = false; if (s.type === 'mouse') start(); };
    const onScroll = () => { if (seen) start(); };
    const offs = [pointerEngine.on('move', onMove), pointerEngine.on('down', onDown), pointerEngine.on('up', onUp)];
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll);
    return () => {
      offs.forEach((o) => o());
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      window.clearTimeout(splitTimer);
      stop();
    };
  }, [reducedMotion, q]);

  return (
    <div ref={root} className="play-reticle" aria-hidden="true">
      <span className="play-reticle__h" /><span className="play-reticle__v" />
      <span className="play-reticle__c play-reticle__c--tl" /><span className="play-reticle__c play-reticle__c--tr" />
      <span className="play-reticle__c play-reticle__c--bl" /><span className="play-reticle__c play-reticle__c--br" />
    </div>
  );
};

export default Reticle;
